import React, { useState } from 'react';
import PaymentStep from './PaymentStep';
import PaymentSteps from './PaymentSteps';
import NewOrderPriceSummaryTable from '../NewItems/NewOrderPriceSummaryTable';
import AdminPaymentDetails from './AdminPaymentDetails';
import OrderModalHeader from './OrderModalHeader';
import { useDealerById, useDistributorById, useTerritoryMetaById } from '../../Store/entities/hooks';
import { useAssembliesByQuoteId } from '../../Store/entities/assemblies/hooks';
import { useQuoteById, useQuotePricing, useQuoteTypeNameByQuoteId } from '../../Store/entities/quotes/hooks';
import { useIsDealer, useIsDistributor, useIsPowerShadesAdmin, useIsRepresentative } from '../../hooks';
import apiCalls from '../../PowerShadesAPIFunctions';
import { fakeRootType } from '../../FakeRootType';

let submit: null | (() => Promise<{
	data?: {
		error?: string;
	};

}>) = null;

type PlaceOrderProps = {
	quoteId: number;
	done: () => void;
	close: () => void;
	ROOT: fakeRootType;
};

type AdminPaymentDetailsState = {
	quote_id: number;
	tax_percentage: number;
	sale_price: number;
	shipping_price: number;
	installation_cost: number;
	misc_cost: number;
	final_price: number;
	multiplier: number;
};

const PlaceOrder = (props: PlaceOrderProps) => {
	const {
		quoteId,
		close,
		ROOT
	} = props;

	const quote = useQuoteById(quoteId);

	const dealerId = quote?.dealer_id ?? 0;
	const dealer = useDealerById(dealerId);

	const distributorId = quote?.distributor_id ?? 0;
	const distributor = useDistributorById(distributorId);

	const repId = quote?.representative_id ?? 0;

	let hasCard = false;
	let hasTerms = false;

	const currentDealerEntity = dealer;

	let multiplier = 1.0;

	const isUserDealer = useIsDealer();
	const isUserDistributor = useIsDistributor();
	const isUserRep = useIsRepresentative();
	const isUserPsAdmin = useIsPowerShadesAdmin();

	if (dealerId !== 0 && (isUserDealer || isUserPsAdmin)) {
		multiplier = currentDealerEntity?.multiplier ?? 1.0;
	} else if (distributorId !== 0 && (isUserDistributor)) {
		multiplier = distributor?.pricing_multiplier ?? 1.0;
	} else if (repId !== 0 && (isUserRep)) {
		multiplier = 1.0;
	}

	if (dealerId) {
		if (currentDealerEntity) {
			hasCard = dealer?.allow_credit_card_payments === true;
			hasTerms = dealer.has_credit_terms === true;
		}
	}

	if (isUserRep || isUserDistributor) {
		hasCard = true;
		hasTerms = true;
	}

	const quotePricing = useQuotePricing(quoteId, multiplier);

	const [showingSubmit, setShowingSubmit] = useState(false);
	const [taxModalOpen] = useState(false);
	const [adminPayment, setAdminPayment] = useState(false);
	const [success, setSuccess] = useState<null | boolean>(null);
	const [adminPaymentDetails, setAdminPaymentDetails] = useState<null | AdminPaymentDetailsState>(null);
	
	const [processing, setProcessing] = useState(false);

	const territory = useTerritoryMetaById(quote?.territory_id ?? 0);

	const outsideContinentalUS = !(territory?.within_continental_usa ?? true);

	const patchAdminDetails = (items: Partial<AdminPaymentDetailsState>) => {
		setAdminPaymentDetails((prev) => ({
			...(prev ?? {} as AdminPaymentDetailsState),
			...items,
		}));
	};

	const enableAdminPaymentDetails = async () => {
		setAdminPayment(true);

		const defaults: AdminPaymentDetailsState = {
			quote_id: quoteId,
			tax_percentage: quote?.sales_tax_percentage_snapshot ?? 0,
			sale_price: quotePricing?.total_price ?? 0.00,
			shipping_price: quotePricing?.total_shipping_price ?? 0,
			installation_cost: 0.00,
			misc_cost: 0.00,
			final_price: 0.00,
			multiplier

		};

		patchAdminDetails(defaults);
	};

	const disableAdminPaymentDetails = () => {
		setAdminPayment(false);
		setAdminPaymentDetails(null);
	};

	const assemblies = useAssembliesByQuoteId(quoteId) ?? [];
	const quoteType = useQuoteTypeNameByQuoteId(quoteId);

	const isRMARWKAndHasNoPreviousQuoteId = (quoteType === "rework" || quoteType === "rma")
		&& assemblies.length > 0
		&& !((quote?.previous_quote_id ?? 0) > 0);

	const onSubmit = () => {
		apiCalls.submitOrderPressed(quoteId).then(() => {
			console.log("submitOrderPressed");
		});
		submit && submit()
			.then((resp) => {
				if (resp && resp.data && resp.data.error) {
					ROOT.alert({ text: `Error Placing Order: ${
						resp.data.error
					}\nPlease contact PowerShades if this issue persists.`,
					quickFormat: "error" });
					setSuccess(null);
				} else {
					setSuccess(true);
					setProcessing(false);
					ROOT.alert({
						title: "Thanks for your order!",
						icon: "confetti",
						smallIcon: false,
						// eslint-disable-next-line @stylistic/max-len
						text: "Your order has been placed successfully - Your quote will be editable for 24 hours. After which, it'll move to manufacturing whenever approved. If you need to cancel your order or any other assistance, please contact us!",
						noCancelButton: true,
						acceptButtonText: "Okay"
					}).then(() => window.location.reload());
				}
			})
			.catch((resp) => {
				if (isRMARWKAndHasNoPreviousQuoteId) {
					ROOT.alert({
						// eslint-disable-next-line @stylistic/max-len
						text: 'Please enter the previous quote id (The PS#) in Advanced Settings to place this RMA/Rework',
						title: 'No Previous Quote Id',
						quickFormat: 'error',
					})
						.then(() => undefined)
						.catch(() => undefined);
				} else {
					ROOT.alert({
						title: "Order Process Failed",
						text: resp.message ? resp.message : resp,
						quickFormat: "error"
					});
				}
			});
	};

	const hasOutdoorAssembleis = assemblies.some((a) => a.shade_type_id === 3);

	return !quote ? (
		<PaymentStep>
			<div>Loading order details…</div>
		</PaymentStep>
	) : (!success && !taxModalOpen
		? (
			<div id="order-confirmation-holder">
				<button
					type="button"
					aria-label="Close place order dialog"
					tabIndex={-1}
					onMouseDown={() => close()}
					onTouchStart={() => close()}
					style={{
						position: 'fixed',
						inset: 0,
						background: 'transparent',
						border: 'none',
						padding: 0,
						margin: 0,
						zIndex: 0,
						cursor: 'default',
					}}
				/>
				<div style={{ position: 'relative', zIndex: 1, overflow: 'hidden auto' }}>
					{(adminPayment && adminPaymentDetails)
						? (
							<AdminPaymentDetails
								close={close}
								back={disableAdminPaymentDetails}
								ROOT={ROOT}
								quoteId={quoteId}
							/>
						)
						: (
							<>
								<OrderModalHeader
									title="Ready to Place an Order?"
									quoteType={quoteType}
									onClose={close}
								/>

								<NewOrderPriceSummaryTable
									quoteId={quoteId}
								/>

								{
									outsideContinentalUS && hasOutdoorAssembleis
										? (
											<span
												style={{
													fontSize: '13px',
													display: 'inline-block',
													marginTop: '1em',
												}}
											>
												* Shipping charges for outdoor shades will be invoiced
												<br />
												&nbsp;&nbsp;separately for orders outside the continental U.S.
												<br />
											</span>
										)
										: null
								}

								{
									showingSubmit
										? (
											<PaymentStep>
												<div id="order-confirm-description">
													Orders will be editable for 24 hours after submission,&nbsp;
													after which they will be processed and uneditable.
												</div>
											</PaymentStep>
										)
										: null
								}

								<PaymentSteps
									quoteType={quoteType}
									hasCard={hasCard}
									hasTerms={hasTerms}
									showingSubmit={showingSubmit}
									processing={processing}
									onSubmit={onSubmit}
									setProcessing={setProcessing}
									ROOT={ROOT}
									quote={quote}
									quoteId={quoteId}
									setShowingSubmit={setShowingSubmit}
									setSubmit={(newSubmit) => {
										submit = newSubmit;
									}}
									setAdminPayment={enableAdminPaymentDetails}
								/>
							</>
						)}
				</div>
			</div>
		) : null);
};

export default PlaceOrder;
